/**
 * Format a date to 'yyyy-MM-dd' in a TIMEZONE-SAFE way.
 * This avoids the common issue where `format(date, 'yyyy-MM-dd')` can shift dates
 * by a day when the Date object is interpreted in UTC vs local time.
 */
export const formatDateToYMD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatDate = (dateString: string): string => {
  // Parse date-only strings as local dates, not UTC
  const date = parseDateString(dateString);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
};

/**
 * Parse a 'YYYY-MM-DD' date string as a local date (not UTC).
 * This prevents the off-by-one-day bug when displaying dates.
 */
export const parseDateString = (dateString: string): Date => {
  // If it's a date-only string (YYYY-MM-DD), parse as local date
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  // Otherwise parse normally (for full ISO strings)
  return new Date(dateString);
};

/**
 * Format a concert date as "Saw on [date]" for past dates
 */
export const formatConcertDate = (dateString: string): string => {
  // Parse date-only strings as local dates, not UTC
  const date = parseDateString(dateString);
  const formattedDate = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
  return `Saw on ${formattedDate}`;
};

/**
 * Get relative time ago (e.g., "5m", "2h", "3d")
 * - Under 60 minutes: "Xm"
 * - 60-119 minutes: "1h"
 * - 120-179 minutes: "2h", etc.
 * - 24+ hours: "1d", "2d", etc.
 */
export const getRelativeTimeAgo = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMinutes < 60) {
    return `${Math.max(1, diffMinutes)}m`;
  } else if (diffHours < 24) {
    return `${diffHours}h`;
  } else {
    return `${diffDays}d`;
  }
};
